import {useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {getServiceRequestsByPhone} from "../../api/client";

const emptyAccountDetails = {
    fullName: "",
    phone: "",
    email: "",
    preferredContact: "PHONE",
    secondaryPhone: "",
    profileNote: "",
    vehicleType: "",
    vehicleBrand: "",
    vehicleModel: "",
    vehicleYear: "",
    vehiclePlate: "",
    vehicleVin: "",
    vehicleMileage: "",
    vehicleComment: "",
};

const statusLabels = {
    NEW: "Новая",
    IN_PROGRESS: "В обработке",
    NEEDS_CLARIFICATION: "Ожидает уточнения",
    ACCEPTED: "Принята",
    IN_WORK: "В работе",
    COMPLETED: "Выполнена",
    CANCELLED: "Отменена",
};

const statusDescriptions = {
    NEW: "Заявка отправлена и ожидает просмотра.",
    IN_PROGRESS: "Заявка уже просматривается администратором.",
    NEEDS_CLARIFICATION: "Сервису нужно уточнить детали перед продолжением.",
    ACCEPTED: "Заявка подтверждена и принята в работу.",
    IN_WORK: "Обращение находится в процессе выполнения.",
    COMPLETED: "Работа по заявке завершена.",
    CANCELLED: "Заявка больше не актуальна.",
};

const statusFilters = [
    {value: "ALL", label: "Все заявки"},
    {value: "NEW", label: "Новые"},
    {value: "IN_PROGRESS", label: "В обработке"},
    {value: "NEEDS_CLARIFICATION", label: "Ожидают уточнения"},
    {value: "ACCEPTED", label: "Принятые"},
    {value: "IN_WORK", label: "В работе"},
    {value: "COMPLETED", label: "Выполненные"},
    {value: "CANCELLED", label: "Отмененные"},
];

const contactMethodLabels = {
    PHONE: "Звонок",
    EMAIL: "Почта",
    TELEGRAM: "Telegram",
    WHATSAPP: "WhatsApp",
    MAX: "Max",
};

const accountSections = [
    {key: "overview", path: "/account", label: "Обзор"},
    {key: "profile", path: "/account/profile", label: "Профиль"},
    {key: "vehicle", path: "/account/vehicle", label: "Моя техника"},
    {key: "requests", path: "/account/requests", label: "Мои заявки"},
    {key: "settings", path: "/account/settings", label: "Настройки"},
];

const profileOptionalFields = ["email", "secondaryPhone", "preferredContact", "profileNote"];
const vehicleFields = [
    "vehicleType",
    "vehicleBrand",
    "vehicleModel",
    "vehicleYear",
    "vehiclePlate",
    "vehicleVin",
    "vehicleMileage",
    "vehicleComment",
];
const vehicleTypeOptions = [
    {value: "", label: "Выберите тип техники"},
    {value: "truck", label: "Грузовой автомобиль"},
    {value: "tractor", label: "Тягач"},
    {value: "trailer", label: "Прицеп / полуприцеп"},
    {value: "bus", label: "Автобус"},
    {value: "special", label: "Спецтехника"},
    {value: "other", label: "Другое"},
];

function formatDate(value) {
    if (!value) {
        return "—";
    }

    return new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(new Date(value));
}

function getAccountDetailsStorageKey(user) {
    if (!user) {
        return null;
    }

    return `truckcenter-account-details-${user.id || user.phone}`;
}

function getCompletedProfileFields(details) {
    return profileOptionalFields.reduce((completedFields, field) => ({
        ...completedFields,
        [field]: Boolean(details[field]),
    }), {});
}

function getVehicleTypeLabel(value) {
    return vehicleTypeOptions.find((option) => option.value === value)?.label || value || "—";
}

function buildVehicleSummary(details) {
    const mainParts = [details.vehicleBrand, details.vehicleModel, details.vehicleYear]
        .filter(Boolean)
        .join(" ");
    const detailParts = [
        details.vehicleType ? getVehicleTypeLabel(details.vehicleType) : "",
        details.vehiclePlate ? `госномер ${details.vehiclePlate}` : "",
        details.vehicleVin ? `VIN ${details.vehicleVin}` : "",
        details.vehicleMileage ? `${details.vehicleMileage} км` : "",
    ].filter(Boolean);

    return [mainParts, ...detailParts].filter(Boolean).join(" · ");
}

function getStatusClassName(status) {
    return (status || "unknown").toLowerCase();
}

function getStatusLabel(status) {
    return statusLabels[status] || status || "Неизвестный статус";
}

function getStatusDescription(status) {
    return statusDescriptions[status] || "Статус заявки обновляется администратором.";
}

function Account({auth, onContactClick, onLogout, section = "overview"}) {
    const user = auth?.user;
    const activeSection = accountSections.some((item) => item.key === section) ? section : "overview";
    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [hideCancelledRequests, setHideCancelledRequests] = useState(false);
    const [accountDetails, setAccountDetails] = useState(emptyAccountDetails);
    const [isDetailsSaved, setIsDetailsSaved] = useState(false);
    const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);
    const [isBaseContactDropdownOpen, setIsBaseContactDropdownOpen] = useState(false);
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const [isVehicleTypeDropdownOpen, setIsVehicleTypeDropdownOpen] = useState(false);
    const [isSaveAnimating, setIsSaveAnimating] = useState(false);
    const [isBaseProfileEditOpen, setIsBaseProfileEditOpen] = useState(false);
    const [isBaseProfileEditClosing, setIsBaseProfileEditClosing] = useState(false);
    const [completedProfileFields, setCompletedProfileFields] = useState({});
    const [baseProfileDraft, setBaseProfileDraft] = useState({
        fullName: "",
        phone: "",
        email: "",
        secondaryPhone: "",
        preferredContact: "PHONE",
        profileNote: "",
    });

    const accountDetailsStorageKey = useMemo(() => getAccountDetailsStorageKey(user), [user]);

    useEffect(() => {
        if (!accountDetailsStorageKey) {
            setAccountDetails(emptyAccountDetails);
            setCompletedProfileFields({});
            return;
        }

        try {
            const storedDetails = window.localStorage.getItem(accountDetailsStorageKey);
            if (storedDetails) {
                const nextDetails = {...emptyAccountDetails, ...JSON.parse(storedDetails)};
                setAccountDetails(nextDetails);
                setCompletedProfileFields(getCompletedProfileFields(nextDetails));
            } else {
                setAccountDetails(emptyAccountDetails);
                setCompletedProfileFields({});
            }
        } catch {
            setAccountDetails(emptyAccountDetails);
            setCompletedProfileFields({});
        }

        setIsDetailsSaved(false);
    }, [accountDetailsStorageKey]);

    useEffect(() => {
        if (!user?.phone) {
            return;
        }

        let isMounted = true;
        setIsLoading(true);
        setError("");

        getServiceRequestsByPhone(user.phone)
            .then((serviceRequests) => {
                if (isMounted) {
                    setRequests(serviceRequests);
                }
            })
            .catch((requestError) => {
                if (isMounted) {
                    setError(requestError.message);
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [user?.phone]);

    const sortedRequests = useMemo(() => {
        return [...requests].sort((firstRequest, secondRequest) => {
            return new Date(secondRequest.createdAt || 0) - new Date(firstRequest.createdAt || 0);
        });
    }, [requests]);

    const filteredRequests = useMemo(() => {
        return sortedRequests.filter((request) => {
            if (hideCancelledRequests && request.status === "CANCELLED") {
                return false;
            }

            if (statusFilter === "ALL") {
                return true;
            }

            return request.status === statusFilter;
        });
    }, [hideCancelledRequests, sortedRequests, statusFilter]);

    const activeRequestsCount = useMemo(() => {
        return requests.filter((request) => ["NEW", "IN_PROGRESS", "NEEDS_CLARIFICATION", "ACCEPTED", "IN_WORK"].includes(request.status)).length;
    }, [requests]);

    const completedRequestsCount = useMemo(() => {
        return requests.filter((request) => request.status === "COMPLETED").length;
    }, [requests]);

    const latestRequest = sortedRequests[0];
    const selectedStatusFilter = statusFilters.find((filter) => filter.value === statusFilter) || statusFilters[0];
    const displayedFullName = accountDetails.fullName || user?.fullName || "";
    const displayedPhone = accountDetails.phone || user?.phone || "";

    const filledProfileItemsCount = [
        completedProfileFields.email,
        completedProfileFields.preferredContact,
        completedProfileFields.secondaryPhone,
        completedProfileFields.profileNote,
    ].filter(Boolean).length;
    const isProfileAdditionalComplete = profileOptionalFields.every((field) => completedProfileFields[field]);

    const filledVehicleItemsCount = vehicleFields.filter((field) => Boolean(accountDetails[field])).length;
    const vehicleSummary = buildVehicleSummary(accountDetails);
    const isVehicleComplete = vehicleFields.every((field) => Boolean(accountDetails[field]));

    function handleAccountDetailsChange(event) {
        const {name, value} = event.target;

        setAccountDetails((currentDetails) => ({
            ...currentDetails,
            [name]: value,
        }));
        setIsDetailsSaved(false);
    }

    function handlePreferredContactSelect(value) {
        setAccountDetails((currentDetails) => ({
            ...currentDetails,
            preferredContact: value,
        }));
        setIsContactDropdownOpen(false);
        setIsDetailsSaved(false);
    }

    function handleBasePreferredContactSelect(value) {
        setBaseProfileDraft((currentDraft) => ({
            ...currentDraft,
            preferredContact: value,
        }));
        setIsBaseContactDropdownOpen(false);
    }

    function handleStatusFilterSelect(value) {
        setStatusFilter(value);
        setIsStatusDropdownOpen(false);
    }

    function handleVehicleTypeSelect(value) {
        setAccountDetails((currentDetails) => ({
            ...currentDetails,
            vehicleType: value,
        }));
        setIsDetailsSaved(false);
        setIsVehicleTypeDropdownOpen(false);
    }

    function triggerSaveAnimation() {
        setIsSaveAnimating(false);
        window.requestAnimationFrame(() => {
            setIsSaveAnimating(true);
        });
        window.setTimeout(() => {
            setIsSaveAnimating(false);
        }, 680);
    }

    function handleAccountDetailsSubmit(event) {
        event.preventDefault();

        if (!accountDetailsStorageKey) {
            return;
        }

        window.localStorage.setItem(accountDetailsStorageKey, JSON.stringify(accountDetails));
        setCompletedProfileFields(getCompletedProfileFields(accountDetails));
        setIsDetailsSaved(true);
        triggerSaveAnimation();
    }

    function handleBaseProfileSubmit(event) {
        event.preventDefault();

        if (!accountDetailsStorageKey) {
            return;
        }

        const nextDetails = {
            ...accountDetails,
            fullName: baseProfileDraft.fullName.trim(),
            phone: baseProfileDraft.phone.trim(),
            email: baseProfileDraft.email.trim(),
            secondaryPhone: baseProfileDraft.secondaryPhone.trim(),
            preferredContact: baseProfileDraft.preferredContact,
            profileNote: baseProfileDraft.profileNote.trim(),
        };

        setAccountDetails(nextDetails);
        window.localStorage.setItem(accountDetailsStorageKey, JSON.stringify(nextDetails));
        setCompletedProfileFields(getCompletedProfileFields(nextDetails));
        setIsDetailsSaved(true);
        closeBaseProfileEdit();
        triggerSaveAnimation();
    }

    function handleBaseProfileDraftChange(event) {
        const {name, value} = event.target;

        setBaseProfileDraft((currentDraft) => ({
            ...currentDraft,
            [name]: value,
        }));
    }

    function openBaseProfileEdit() {
        setBaseProfileDraft({
            fullName: displayedFullName,
            phone: displayedPhone,
            email: accountDetails.email,
            secondaryPhone: accountDetails.secondaryPhone,
            preferredContact: accountDetails.preferredContact,
            profileNote: accountDetails.profileNote,
        });
        setIsBaseProfileEditClosing(false);
        setIsBaseContactDropdownOpen(false);
        setIsBaseProfileEditOpen(true);
    }

    function closeBaseProfileEdit() {
        if (!isBaseProfileEditOpen || isBaseProfileEditClosing) {
            return;
        }

        setIsBaseProfileEditClosing(true);
        setIsBaseContactDropdownOpen(false);
        window.setTimeout(() => {
            setIsBaseProfileEditOpen(false);
            setIsBaseProfileEditClosing(false);
        }, 220);
    }

    function renderSectionNav() {
        return (
            <nav className="account-section-nav" aria-label="Разделы личного кабинета">
                {accountSections.map((item) => (
                    <Link
                        className={activeSection === item.key ? "account-section-link-active" : ""}
                        key={item.key}
                        to={item.path}
                    >
                        {item.label}
                    </Link>
                ))}
            </nav>
        );
    }

    function renderSummary() {
        return (
            <section className="account-summary" aria-label="Сводка личного кабинета">
                <article className="account-summary-card">
                    <span>Всего заявок</span>
                    <strong>{requests.length}</strong>
                    <small>История обращений по вашему телефону</small>
                </article>
                <article className="account-summary-card">
                    <span>Активные</span>
                    <strong>{activeRequestsCount}</strong>
                    <small>Новые и находящиеся в работе</small>
                </article>
                <article className="account-summary-card">
                    <span>Завершенные</span>
                    <strong>{completedRequestsCount}</strong>
                    <small>Закрытые обращения</small>
                </article>
                <article className="account-summary-card">
                    <span>Последний статус</span>
                    <strong>{latestRequest ? statusLabels[latestRequest.status] || latestRequest.status : "—"}</strong>
                    <small>{latestRequest ? formatDate(latestRequest.createdAt) : "Заявок пока нет"}</small>
                </article>
            </section>
        );
    }

    function renderSaveButton() {
        return (
            <div className="account-form-actions">
                <button
                    className={isSaveAnimating ? "account-button account-save-button account-save-button-animated" : "account-button account-save-button"}
                    type="submit"
                >
                    Сохранить данные
                </button>
                <span>{isDetailsSaved ? "Данные сохранены в браузере" : "Заполните данные и сохраните изменения"}</span>
            </div>
        );
    }

    function renderOverview() {
        return (
            <>
                {renderSummary()}
                <section className="account-section">
                    <div className="account-section-header">
                        <span>Обзор</span>
                        <h2>Разделы личного кабинета</h2>
                        <p>Выберите нужный раздел. Каждый пункт открывается отдельной страницей без скролла по одной длинной странице.</p>
                    </div>
                    <div className="account-overview-grid">
                        {accountSections.filter((item) => item.key !== "overview").map((item) => (
                            <Link className="account-overview-card" key={item.key} to={item.path}>
                                <strong>{item.label}</strong>
                                <span>Открыть страницу</span>
                            </Link>
                        ))}
                    </div>
                </section>
            </>
        );
    }

    function renderProfile() {
        return (
            <form className="account-details-form account-sections" onSubmit={handleAccountDetailsSubmit}>
                <section className="account-section">
                    <div className="account-section-header">
                        <span>Раздел</span>
                        <h2>Профиль</h2>
                        <p>Основные данные клиента и удобный способ связи для уточнения деталей.</p>
                    </div>
                    <div className="account-section-grid">
                        <article className="account-card account-profile-current-card">
                            <div className="account-card-heading">
                                <span>Текущие данные</span>
                            </div>
                            <dl className="account-profile-list">
                                <div>
                                    <dt>ФИО</dt>
                                    <dd>{displayedFullName}</dd>
                                </div>
                                <div>
                                    <dt>Телефон</dt>
                                    <dd>{displayedPhone}</dd>
                                </div>
                                {completedProfileFields.email && (
                                    <div>
                                        <dt>Email</dt>
                                        <dd>{accountDetails.email}</dd>
                                    </div>
                                )}
                                {completedProfileFields.secondaryPhone && (
                                    <div>
                                        <dt>Доп. телефон</dt>
                                        <dd>{accountDetails.secondaryPhone}</dd>
                                    </div>
                                )}
                                {completedProfileFields.preferredContact && (
                                    <div>
                                        <dt>Удобная связь</dt>
                                        <dd>{contactMethodLabels[accountDetails.preferredContact] || "Не указана"}</dd>
                                    </div>
                                )}
                                {completedProfileFields.profileNote && (
                                    <div>
                                        <dt>Комментарий</dt>
                                        <dd>{accountDetails.profileNote}</dd>
                                    </div>
                                )}
                            </dl>
                            <button
                                className="account-button account-profile-edit-button"
                                type="button"
                                onClick={openBaseProfileEdit}
                            >
                                Редактировать существующие данные
                            </button>
                        </article>

                        <article className="account-card">
                            <div className="account-card-heading">
                                <span>Дополнительная информация</span>
                                <strong>{filledProfileItemsCount}/4 заполнено</strong>
                            </div>
                            {isProfileAdditionalComplete ? (
                                <div className="account-profile-complete">
                                    <span>Профиль заполнен</span>
                                    <h3>Поздравляем! Теперь ваш профиль заполнен.</h3>
                                    <p>Можно оставить заявку на ремонт или заполнить раздел “Моя техника”, чтобы сервису было проще оценить задачу.</p>
                                    <div className="account-profile-complete-actions">
                                        <Link className="account-button" to="/request">Оставить заявку</Link>
                                        <Link className="account-button account-button-secondary" to="/account/vehicle">Заполнить технику</Link>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <fieldset>
                                        <legend>Дополнительная информация</legend>
                                        {!completedProfileFields.email && (
                                            <label>
                                                <span>Email</span>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={accountDetails.email}
                                                    onChange={handleAccountDetailsChange}
                                                    placeholder="example@mail.ru"
                                                />
                                            </label>
                                        )}
                                        {!completedProfileFields.secondaryPhone && (
                                            <label>
                                                <span>Дополнительный телефон</span>
                                                <input
                                                    type="tel"
                                                    name="secondaryPhone"
                                                    value={accountDetails.secondaryPhone}
                                                    onChange={handleAccountDetailsChange}
                                                    placeholder="+7 950 000 00 00"
                                                    inputMode="tel"
                                                    maxLength="20"
                                                />
                                            </label>
                                        )}
                                        {!completedProfileFields.preferredContact && (
                                            <div className="account-contact-field">
                                                <span>Удобный способ связи</span>
                                                <div
                                                    className="account-contact-dropdown"
                                                    data-open={isContactDropdownOpen}
                                                    onBlur={(event) => {
                                                        if (!event.currentTarget.contains(event.relatedTarget)) {
                                                            setIsContactDropdownOpen(false);
                                                        }
                                                    }}
                                                >
                                                    <button
                                                        className="account-contact-toggle"
                                                        type="button"
                                                        aria-expanded={isContactDropdownOpen}
                                                        aria-haspopup="listbox"
                                                        onClick={() => setIsContactDropdownOpen((isOpen) => !isOpen)}
                                                    >
                                                        <span className="account-dropdown-value" key={accountDetails.preferredContact}>
                                                            {contactMethodLabels[accountDetails.preferredContact]}
                                                        </span>
                                                        <span className="account-contact-arrow" aria-hidden="true"/>
                                                    </button>
                                                    <div className="account-contact-list" role="listbox" aria-label="Удобный способ связи">
                                                        {Object.entries(contactMethodLabels).map(([value, label]) => (
                                                            <button
                                                                className="account-contact-option"
                                                                data-selected={accountDetails.preferredContact === value}
                                                                key={value}
                                                                type="button"
                                                                role="option"
                                                                aria-selected={accountDetails.preferredContact === value}
                                                                onClick={() => handlePreferredContactSelect(value)}
                                                            >
                                                                {label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {!completedProfileFields.profileNote && (
                                            <label className="account-field-wide">
                                                <span>Комментарий к профилю</span>
                                                <textarea
                                                    name="profileNote"
                                                    value={accountDetails.profileNote}
                                                    onChange={handleAccountDetailsChange}
                                                    placeholder="Например: удобнее писать в мессенджер после 18:00"
                                                    rows="3"
                                                />
                                            </label>
                                        )}
                                    </fieldset>
                                    {renderSaveButton()}
                                </>
                            )}
                        </article>

                        <article className="account-card account-profile-help-card">
                            <div className="account-card-heading">
                                <span>Зачем заполнять профиль</span>
                            </div>
                            <ul className="account-help-list">
                                <li>Контакты помогают быстрее связаться с вами после заявки.</li>
                                <li>Удобный способ связи снижает количество лишних уточнений.</li>
                                <li>Комментарий к профилю полезен, если есть ограничения по времени или каналу связи.</li>
                            </ul>
                        </article>
                    </div>
                </section>
            </form>
        );
    }

    function renderVehicle() {
        return (
            <form className="account-details-form account-sections" onSubmit={handleAccountDetailsSubmit}>
                <section className="account-section">
                    <div className="account-section-header">
                        <span>Раздел</span>
                        <h2>Моя техника</h2>
                        <p>Параметры автомобиля или грузовой техники, чтобы не вводить их заново в каждой заявке.</p>
                    </div>
                    <div className="account-section-grid">
                        <article className="account-card account-card-wide">
                            <div className="account-card-heading">
                                <span>Данные техники</span>
                                <strong>{filledVehicleItemsCount}/{vehicleFields.length} заполнено</strong>
                            </div>
                            <fieldset>
                                <legend>Основные параметры</legend>
                                <div className="account-vehicle-type-field">
                                    <span>Тип техники</span>
                                    <div
                                        className="account-vehicle-type-dropdown"
                                        data-open={isVehicleTypeDropdownOpen}
                                        onBlur={(event) => {
                                            if (!event.currentTarget.contains(event.relatedTarget)) {
                                                setIsVehicleTypeDropdownOpen(false);
                                            }
                                        }}
                                    >
                                        <button
                                            className="account-vehicle-type-toggle"
                                            type="button"
                                            aria-expanded={isVehicleTypeDropdownOpen}
                                            aria-haspopup="listbox"
                                            onClick={() => setIsVehicleTypeDropdownOpen((isOpen) => !isOpen)}
                                        >
                                            <span className="account-dropdown-value" key={accountDetails.vehicleType}>
                                                {getVehicleTypeLabel(accountDetails.vehicleType)}
                                            </span>
                                            <span className="account-contact-arrow" aria-hidden="true"/>
                                        </button>
                                        <div className="account-vehicle-type-list" role="listbox" aria-label="Тип техники">
                                            {vehicleTypeOptions.map((option) => (
                                                <button
                                                    className="account-vehicle-type-option"
                                                    data-selected={accountDetails.vehicleType === option.value}
                                                    key={option.value}
                                                    type="button"
                                                    role="option"
                                                    aria-selected={accountDetails.vehicleType === option.value}
                                                    onClick={() => handleVehicleTypeSelect(option.value)}
                                                >
                                                    <span>{option.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <label>
                                    <span>Марка</span>
                                    <input
                                        type="text"
                                        name="vehicleBrand"
                                        value={accountDetails.vehicleBrand}
                                        onChange={handleAccountDetailsChange}
                                        placeholder="Например: КамАЗ"
                                    />
                                </label>
                                <label>
                                    <span>Модель</span>
                                    <input
                                        type="text"
                                        name="vehicleModel"
                                        value={accountDetails.vehicleModel}
                                        onChange={handleAccountDetailsChange}
                                        placeholder="Например: 5490"
                                    />
                                </label>
                                <label>
                                    <span>Год выпуска</span>
                                    <input
                                        type="text"
                                        name="vehicleYear"
                                        value={accountDetails.vehicleYear}
                                        onChange={handleAccountDetailsChange}
                                        placeholder="2021"
                                        inputMode="numeric"
                                        maxLength="4"
                                    />
                                </label>
                                <label>
                                    <span>Госномер</span>
                                    <input
                                        type="text"
                                        name="vehiclePlate"
                                        value={accountDetails.vehiclePlate}
                                        onChange={handleAccountDetailsChange}
                                        placeholder="Например: А123БВ"
                                        maxLength="20"
                                    />
                                </label>
                                <label>
                                    <span>VIN</span>
                                    <input
                                        type="text"
                                        name="vehicleVin"
                                        value={accountDetails.vehicleVin}
                                        onChange={handleAccountDetailsChange}
                                        placeholder="VIN при наличии"
                                        maxLength="32"
                                    />
                                </label>
                                <label>
                                    <span>Пробег (в км)</span>
                                    <input
                                        type="text"
                                        name="vehicleMileage"
                                        value={accountDetails.vehicleMileage}
                                        onChange={handleAccountDetailsChange}
                                        placeholder="Например: 250000"
                                        inputMode="numeric"
                                        maxLength="9"
                                    />
                                </label>
                                <label className="account-field-wide">
                                    <span>Дополнительная информация</span>
                                    <textarea
                                        name="vehicleComment"
                                        value={accountDetails.vehicleComment}
                                        onChange={handleAccountDetailsChange}
                                        placeholder="Особенности техники, частые неисправности, ограничения по эксплуатации"
                                        rows="3"
                                    />
                                </label>
                            </fieldset>
                            {renderSaveButton()}
                        </article>

                        <article className="account-card account-vehicle-preview-card">
                            <div className="account-card-heading">
                                <span>Сохраненный автомобиль</span>
                            </div>
                            {vehicleSummary ? (
                                <>
                                    <strong>{vehicleSummary}</strong>
                                    <p>Эти данные будут доступны при создании новой заявки, чтобы не вводить технику вручную.</p>
                                    {isVehicleComplete && (
                                        <div className="account-profile-complete account-vehicle-complete">
                                            <span>Профиль техники заполнен</span>
                                            <p>Теперь при обращении сервису проще понять параметры автомобиля заранее.</p>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <strong>Техника пока не указана</strong>
                                    <p>Добавьте хотя бы марку, модель и год выпуска. Это ускорит обработку заявки.</p>
                                </>
                            )}
                        </article>

                        <article className="account-card account-profile-help-card">
                            <div className="account-card-heading">
                                <span>Что будет дальше</span>
                            </div>
                            <ul className="account-help-list">
                                <li>Сейчас в кабинете хранится один основной автомобиль.</li>
                                <li>Позже можно будет добавить несколько автомобилей и выбирать нужный при заявке.</li>
                                <li>Сохраненная техника уже подставляется в форму новой заявки.</li>
                            </ul>
                        </article>
                    </div>
                </section>
            </form>
        );
    }

    function renderRequests() {
        return (
            <section className="account-section">
                <div className="account-section-header">
                    <span>Раздел</span>
                    <h2>Мои заявки</h2>
                    <p>Создание новой заявки, история обращений, статусы и краткие данные по каждой заявке.</p>
                </div>
                <div className="account-action-layout">
                    <article className="account-card account-action-card">
                        <div className="account-card-heading">
                            <span>Новая заявка</span>
                        </div>
                        <p>Оставьте обращение по ремонту, диагностике или обслуживанию. Оно попадет в админку.</p>
                        <Link className="account-button" to="/request">Перейти к форме заявки</Link>
                    </article>

                    <article className="account-card account-action-card">
                        <div className="account-card-heading">
                            <span>Быстрая связь</span>
                        </div>
                        <p>Если вопрос срочный, лучше написать напрямую или позвонить. Контакты проверяются чаще заявок.</p>
                        <button className="account-button account-button-secondary" type="button" onClick={onContactClick}>
                            Открыть контакты
                        </button>
                    </article>
                </div>

                <article className="account-card">
                    <div className="account-card-heading account-card-heading-with-controls">
                        <span>Список заявок</span>
                        <div className="account-filter">
                            <span>Статус</span>
                            <div
                                className="account-status-dropdown"
                                data-open={isStatusDropdownOpen}
                                onBlur={(event) => {
                                    if (!event.currentTarget.contains(event.relatedTarget)) {
                                        setIsStatusDropdownOpen(false);
                                    }
                                }}
                            >
                                <button
                                    className="account-status-toggle"
                                    type="button"
                                    aria-expanded={isStatusDropdownOpen}
                                    aria-haspopup="listbox"
                                    onClick={() => setIsStatusDropdownOpen((isOpen) => !isOpen)}
                                >
                                    <span className="account-dropdown-value" key={statusFilter}>
                                        {selectedStatusFilter.label}
                                    </span>
                                    <span className="account-contact-arrow" aria-hidden="true"/>
                                </button>
                                <div className="account-status-list" role="listbox" aria-label="Статус заявки">
                                    {statusFilters.map((filter) => (
                                        <button
                                            className={`account-status-option account-status-option-${getStatusClassName(filter.value)}`}
                                            data-selected={statusFilter === filter.value}
                                            key={filter.value}
                                            type="button"
                                            role="option"
                                            aria-selected={statusFilter === filter.value}
                                            onClick={() => handleStatusFilterSelect(filter.value)}
                                        >
                                            {filter.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {isLoading && <p className="account-status">Загружаем заявки...</p>}
                    {error && <p className="account-status account-status-error">{error}</p>}

                    <div className="account-request-toolbar">
                        <button
                            className={hideCancelledRequests ? "account-request-toggle account-request-toggle-active" : "account-request-toggle"}
                            type="button"
                            onClick={() => setHideCancelledRequests((isHidden) => !isHidden)}
                        >
                            {hideCancelledRequests ? "Показать отмененные" : "Скрыть отмененные"}
                        </button>
                    </div>

                    {!isLoading && !error && requests.length === 0 && (
                        <div className="account-empty">
                            <h2>Заявок пока нет</h2>
                            <p>Оставьте заявку через кнопку “Новая заявка”, и она появится здесь.</p>
                        </div>
                    )}

                    {!isLoading && !error && requests.length > 0 && filteredRequests.length === 0 && (
                        <div className="account-empty">
                            <h2>По выбранному статусу заявок нет</h2>
                            <p>Смените фильтр, чтобы посмотреть всю историю обращений.</p>
                        </div>
                    )}

                    {!isLoading && !error && filteredRequests.length > 0 && (
                        <div className="account-request-list">
                            {filteredRequests.map((request) => {
                                const statusClassName = getStatusClassName(request.status);

                                return (
                                    <article className={`account-request-card account-request-card-${statusClassName}`} key={request.id}>
                                        <div>
                                            <span className={`account-request-status account-request-status-${statusClassName}`}>
                                                {getStatusLabel(request.status)}
                                            </span>
                                            <h2>{request.serviceType || "Заявка на обслуживание"}</h2>
                                            <p>{request.message || "Комментарий не указан"}</p>
                                            <p className="account-request-status-description">
                                                {getStatusDescription(request.status)}
                                            </p>
                                        </div>
                                        <dl>
                                            <div>
                                                <dt>Техника</dt>
                                                <dd>{request.vehicle || "—"}</dd>
                                            </div>
                                            <div>
                                                <dt>Создана</dt>
                                                <dd>{formatDate(request.createdAt)}</dd>
                                            </div>
                                            <div>
                                                <dt>Номер заявки</dt>
                                                <dd>#{request.id}</dd>
                                            </div>
                                        </dl>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </article>
            </section>
        );
    }

    function renderSettings() {
        return (
            <section className="account-section">
                <div className="account-section-header">
                    <span>Раздел</span>
                    <h2>Настройки</h2>
                </div>
            </section>
        );
    }

    function renderBaseProfileModal() {
        if (!isBaseProfileEditOpen) {
            return null;
        }

        return (
            <div
                className="account-modal"
                role="presentation"
                data-state={isBaseProfileEditClosing ? "closing" : "open"}
                onClick={closeBaseProfileEdit}
            >
                <form
                    className="account-modal-panel"
                    onSubmit={handleBaseProfileSubmit}
                    onClick={(event) => event.stopPropagation()}
                >
                    <button
                        className="account-modal-close"
                        type="button"
                        aria-label="Закрыть окно редактирования"
                        onClick={closeBaseProfileEdit}
                    >
                        ×
                    </button>
                    <div className="account-modal-heading">
                        <span>Основные данные</span>
                        <h2>Редактировать существующие данные</h2>
                        <p>Эти данные обязательные и были указаны при регистрации. Дополнительную информацию заполняйте в карточке профиля.</p>
                    </div>
                    <label>
                        <span>ФИО</span>
                        <input
                            type="text"
                            name="fullName"
                            value={baseProfileDraft.fullName}
                            onChange={handleBaseProfileDraftChange}
                            placeholder={user.fullName}
                            maxLength="120"
                            required
                        />
                    </label>
                    <label>
                        <span>Телефон</span>
                        <input
                            type="tel"
                            name="phone"
                            value={baseProfileDraft.phone}
                            onChange={handleBaseProfileDraftChange}
                            placeholder={user.phone}
                            inputMode="tel"
                            maxLength="20"
                            required
                        />
                    </label>
                    <label>
                        <span>Email</span>
                        <input
                            type="email"
                            name="email"
                            value={baseProfileDraft.email}
                            onChange={handleBaseProfileDraftChange}
                            placeholder="example@mail.ru"
                        />
                    </label>
                    <label>
                        <span>Дополнительный телефон</span>
                        <input
                            type="tel"
                            name="secondaryPhone"
                            value={baseProfileDraft.secondaryPhone}
                            onChange={handleBaseProfileDraftChange}
                            placeholder="+7 950 000 00 00"
                            inputMode="tel"
                            maxLength="20"
                        />
                    </label>
                    <div className="account-modal-contact-field">
                        <span>Удобный способ связи</span>
                        <div
                            className="account-contact-dropdown account-modal-contact-dropdown"
                            data-open={isBaseContactDropdownOpen}
                            onBlur={(event) => {
                                if (!event.currentTarget.contains(event.relatedTarget)) {
                                    setIsBaseContactDropdownOpen(false);
                                }
                            }}
                        >
                            <button
                                className="account-contact-toggle"
                                type="button"
                                aria-expanded={isBaseContactDropdownOpen}
                                aria-haspopup="listbox"
                                onClick={() => setIsBaseContactDropdownOpen((isOpen) => !isOpen)}
                            >
                                <span className="account-dropdown-value" key={baseProfileDraft.preferredContact}>
                                    {contactMethodLabels[baseProfileDraft.preferredContact]}
                                </span>
                                <span className="account-contact-arrow" aria-hidden="true"/>
                            </button>
                            <div className="account-contact-list" role="listbox" aria-label="Удобный способ связи">
                                {Object.entries(contactMethodLabels).map(([value, label]) => (
                                    <button
                                        className="account-contact-option"
                                        data-selected={baseProfileDraft.preferredContact === value}
                                        key={value}
                                        type="button"
                                        role="option"
                                        aria-selected={baseProfileDraft.preferredContact === value}
                                        onClick={() => handleBasePreferredContactSelect(value)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <label>
                        <span>Комментарий к профилю</span>
                        <textarea
                            name="profileNote"
                            value={baseProfileDraft.profileNote}
                            onChange={handleBaseProfileDraftChange}
                            placeholder="Например: удобнее писать в мессенджер после 18:00"
                            rows="3"
                        />
                    </label>
                    <div className="account-modal-actions">
                        <button className="account-button" type="submit">Сохранить</button>
                        <button
                            className="account-button account-button-secondary"
                            type="button"
                            onClick={closeBaseProfileEdit}
                        >
                            Отмена
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    function renderActiveSection() {
        if (activeSection === "profile") {
            return renderProfile();
        }

        if (activeSection === "vehicle") {
            return renderVehicle();
        }

        if (activeSection === "requests") {
            return renderRequests();
        }

        if (activeSection === "settings") {
            return renderSettings();
        }

        return renderOverview();
    }

    if (!user) {
        return (
            <main className="account-page">
                <section className="account-hero">
                    <span className="account-eyebrow">Личный кабинет</span>
                    <h1>Нужен вход</h1>
                    <p>Войдите или зарегистрируйтесь, чтобы видеть свои заявки и статус работ.</p>
                    <div className="account-actions">
                        <Link className="account-button" to="/login">Войти</Link>
                        <Link className="account-button account-button-secondary" to="/register">Регистрация</Link>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main className="account-page">
            <section className="account-hero">
                <div>
                    <span className="account-eyebrow">Личный кабинет</span>
                    <h1>{displayedFullName}</h1>
                    <p>Контролируйте заявки, дополняйте данные профиля и храните параметры техники для быстрых обращений.</p>
                </div>
                <div className="account-actions">
                    <Link className="account-button" to="/request">
                        Новая заявка
                    </Link>
                    <button className="account-button account-button-secondary" type="button" onClick={onLogout}>
                        Выйти
                    </button>
                </div>
            </section>

            {renderSectionNav()}
            <div className="account-section-page" key={activeSection}>
                {renderActiveSection()}
            </div>
            {renderBaseProfileModal()}
        </main>
    );
}

export default Account;
